import { LANGUAGE_CATALOG } from '../config/languages.catalog';
import { companyService } from './company.service';
import { authService } from './auth.service';
import { TranslatedAxiosError } from '../services/migrated/axiosWrapper';
import { TranslateFn } from '../utils/translate_fn.types';
import { resolveLangValue } from '@root/app/utilities/multilanguage.utilities';

type LanguagesCallback<T = unknown> = (err: TranslatedAxiosError | null, result?: T) => void;

export interface MultiLanguageItem {
    defaultValue: string;
    langValues?: Record<string, string>;
}

export const APP_LANGUAGE_ENTRIES = [
    { id: 'en', labelKey: 'TRANSLATE_EN' },
    { id: 'es', labelKey: 'TRANSLATE_ES' },
    { id: 'de', labelKey: 'TRANSLATE_DE' },
    { id: 'fr', labelKey: 'TRANSLATE_FR' },
    { id: 'it', labelKey: 'TRANSLATE_IT' },
    { id: 'pl', labelKey: 'TRANSLATE_PL' },
    { id: 'pt-BR', labelKey: 'TRANSLATE_PT_BR' },
    { id: 'pt', labelKey: 'TRANSLATE_PT_PT' },
    { id: 'ru', labelKey: 'TRANSLATE_RU' }
] as const;

let cachedCompanyName: string | null = null;

export const getNameByCode = (code: string): string | null => {
    const match = LANGUAGE_CATALOG.find((lang) => lang.code === code);
    return match ? match.language : null;
};

export const getAppLanguages = (translate: TranslateFn) =>
    APP_LANGUAGE_ENTRIES.map(({ id, labelKey }) => ({
        id,
        label: translate(labelKey)
    }));

export const getSupportedLanguages = (callback: LanguagesCallback<{ code: string; language: string }[]>): void => {
    companyService.getCurrentCompanyConfig((err, companyConfig) => {
        if (err || !companyConfig) {
            callback(err);
            return;
        }
        const supportedLanguages = (companyConfig.supportedLanguages || [])
            .filter((lang) => companyConfig.defaultLanguage !== lang)
            .map((lang) => ({ code: lang, language: getNameByCode(lang) }));
        callback(null, supportedLanguages);
    });
};

export const replaceTokens = (
    input: string,
    callback: LanguagesCallback<string>,
    translate?: TranslateFn
): void => {
    if (!input || !input.includes('*|COMPANY_NAME|*')) {
        callback(null, input);
        return;
    }

    if (cachedCompanyName) {
        callback(null, input.replaceAll('*|COMPANY_NAME|*', cachedCompanyName));
        return;
    }

    companyService.getUserCompany((err, company) => {
        const fallback = translate ? translate('COMPANY') : 'COMPANY';
        cachedCompanyName = err ? fallback : company?.name || fallback;
        callback(null, input.replaceAll('*|COMPANY_NAME|*', cachedCompanyName));
    });
};

export const getTranslationFromUserLanguage = (
    languageItem: string | MultiLanguageItem | undefined,
    userLanguage?: string
): string => {
    if (typeof languageItem === 'string') {
        return languageItem;
    }
    if (!languageItem || typeof languageItem !== 'object') {
        return '';
    }

    const lang = userLanguage || authService.getLanguage();
    const translated = resolveLangValue(languageItem.langValues, lang);
    if (translated !== undefined) {
        return translated;
    }

    if (languageItem.defaultValue || languageItem.defaultValue === '') {
        return languageItem.defaultValue;
    }
    return '';
};

export const initializeOrGetLangValue = (langItem: MultiLanguageItem, language?: string): string => {
    if (language) {
        if (!langItem.langValues) {
            langItem.langValues = {};
        }
        if (!langItem.langValues[language]) {
            langItem.langValues[language] = '';
        }
        return langItem.langValues[language];
    }
    return langItem.defaultValue;
};

export const setLangValue = (langItem: MultiLanguageItem, value: string, language?: string): void => {
    if (language) {
        if (!langItem.langValues) {
            langItem.langValues = {};
        }
        langItem.langValues[language] = value;
    } else {
        langItem.defaultValue = value;
    }
};

export const removeLangValue = (langItem: MultiLanguageItem, language: string): void => {
    if (language && langItem.langValues) {
        delete langItem.langValues[language];
    }
};

export const getLangs = (langItem: MultiLanguageItem): string[] => Object.keys(langItem.langValues || {});

export const createMultiLanguage = (): MultiLanguageItem => ({
    defaultValue: '',
    langValues: {}
});

export const languagesService = {
    list: LANGUAGE_CATALOG,
    getNameByCode,
    getAppLanguages,
    getSupportedLanguages,
    replaceTokens,
    getTranslationFromUserLanguage,
    appLangCodes: ['en', 'es', 'de', 'fr', 'it', 'pl', 'pt-BR', 'pt', 'ru'],
    initializeOrGetLangValue,
    setLangValue,
    removeLangValue,
    getLangs,
    createMultiLanguage
};
